import React, { useMemo, useState } from 'react';
import {
	useSimpleWorkerInformationQuery,
	useWorkerWorkplacesQuery,
	WorkplaceMember,
} from '../../../../../graphql/graphql';
import Loading from '../../../../../Components/Loading';
import UserAvatarImage from '../../../../../Components/UserAvatar/UserAvatarImage';
// @ts-ignore
import styles from './WorkplaceWorkerDetail.module.scss';
import TherapistDetail from '../../Workers/TherapistDetail/TherapistDetail';
import WorkerDetail from '../../Workers/WorkerDetail/WorkerDetail';
import { useAppContext } from '../../../../../Services/AppContext';
import Text from '../../../../../Components/Base/Text/Text';
import WorkplaceWorkerWorkplacesList from './WorkplaceWorkerWorkplacesList';
import useTranslation from '../../../../../Hooks/useTranslation';
import WorkplaceWorkerSUKLLogin from '../WorkplaceWorkerSUKLLogin/WorkplaceWorkerSUKLLogin';
import useScope from '../../../../Scope/Hooks/useScope';
import WorkplaceWorkerHealthCareWorkerCode from '../WorkplaceWorkerHealthCareWorkerCode/WorkplaceWorkerHealthCareWorkerCode';

interface Props {
	userId: string;
	showAllWorkplaces: boolean;
	setDoListRefresh: (doListRefresh: boolean) => void;
}

const WorkplaceWorkerDetail: React.FC<Props> = ({
	userId,
	showAllWorkplaces,
	setDoListRefresh,
}) => {
	const { t } = useTranslation();
	const { isLatam, workplaceId, isMou } = useAppContext();
	const { isSlovakia } = useScope();
	const { data: userData, isLoading: isLoadingUserData } =
		useSimpleWorkerInformationQuery({ userId });
	const {
		data: workersWorkplaces,
		isLoading: isLoadingWorkersWorkplaces,
		refetch: reloadWorkersWorkplaces,
	} = useWorkerWorkplacesQuery({
		userId,
		rootWorkplaceId: workplaceId,
	});
	const [isTherapist, setIsTherapist] = useState(false);

	const displayedWorkplaces = useMemo(() => {
		const temp: WorkplaceMember[] = [];
		workersWorkplaces?.workplaceMembers?.data?.forEach(item => {
			if (showAllWorkplaces) {
				temp.push(item as WorkplaceMember);
			} else if (item?.workplace?.id === workplaceId) {
				temp.push(item as WorkplaceMember);
			}
			setIsTherapist(
				item?.workplaceMemberRoles?.some(
					workplaceMemberRole => workplaceMemberRole?.role?.key === 'therapist',
				) ?? false,
			);
		});
		return temp;
	}, [workersWorkplaces]);

	const handleOnUserWorkplacesUpdate = () => {
		setDoListRefresh(true);
		reloadWorkersWorkplaces();
	};

	if (isLoadingUserData || isLoadingWorkersWorkplaces) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.left}>
				<UserAvatarImage
					userId={userId}
					profileImageUrl={userData?.user?.profileImageUrl}
				/>
				<div className={styles.name}>{userData?.user?.profile?.fullName}</div>
				<div>{userData?.user?.email}</div>
				<div>{userData?.user?.formattedPhoneNumber}</div>
			</div>
			<div className={styles.right}>
				{isTherapist && (
					<div className={styles.part}>
						<div className={styles.title}>
							{t('component.doctorDetailComponent.therapistSettings')}:
						</div>
						<TherapistDetail userId={userId} />
					</div>
				)}
				{isLatam && (
					<div className={styles.part}>
						<Text tx='base.shared.profile' className={styles.title} />
						<WorkerDetail userId={userId} />
					</div>
				)}
				{isMou && (
					<div className={styles.part}>
						<Text
							tx='component.userSuklLoginCredentials.title'
							className={styles.title}
						/>
						<WorkplaceWorkerSUKLLogin userId={userId} />
					</div>
				)}
				{isSlovakia && !showAllWorkplaces && (
					<div className={styles.part}>
						<WorkplaceWorkerHealthCareWorkerCode userId={userId} />
					</div>
				)}
				<div className={styles.part}>
					<WorkplaceWorkerWorkplacesList
						data={displayedWorkplaces}
						showAllWorkplaces={showAllWorkplaces}
						userId={userId}
						onUpdate={handleOnUserWorkplacesUpdate}
					/>
				</div>
			</div>
		</div>
	);
};

export default WorkplaceWorkerDetail;
